import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo, Suspense, lazy } from "react";
import "./App.css";
import "./styles/scroll-optimization.css";
import "./styles/invite-engine.css";
import { BrowserRouter, Routes, Route, useSearchParams, useLocation, useNavigate, useParams, Navigate } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import { CityProvider } from "./contexts/CityContext";
import { ToastProvider } from "./components/shared/ToastNotification";
import { ThemeProvider } from "./contexts/ThemeContext";
import { UnifiedProvider, useUnified } from "./contexts/UnifiedContext";

// Keep frequently used components eagerly loaded
import SlideOutMenu from "./components/SlideOutMenu";
// FloatingSafetyIcon removed - SOS moved to slide-out menu
import WhatsNewModal from "./components/shared/WhatsNewModal";
import AuthPrompt from "./components/auth/AuthPrompt";
import CheckInFollowupInbox from "./components/shared/CheckInFollowupInbox";
import AnnouncementPrompt from "./components/shared/AnnouncementPrompt";
import SharedFooter from "./components/shared/SharedFooter";
import InviteFriendsModal from "./components/invite/InviteFriendsModal";
import AskStagsterSheet from "./components/shared/AskStagsterSheet";
import StagsterPassportModal from "./components/profile/StagsterPassportModal";
import FastLaneCheckinModal from "./components/venue-portal/FastLaneCheckinModal";
import VenuePortalLogin from "./components/venue-portal/VenuePortalLogin";

import ErrorBoundary from "./components/ErrorBoundary";
import LoadingSpinner from "./components/LoadingSpinner";
import Homepage2026 from "./components/pages/Homepage2026";
import Logo from "./components/Logo";
import { Home, Radar, Beer, Briefcase, Lock, MessageCircle } from "lucide-react";
import { inboxHomeAPI, getAPIRoot } from './services/api';
import { referralStorage } from './services/referralService';
import { setupNativeApp, registerNativePushToken } from './lib/nativeApp';
import { useToast } from './components/shared/ToastNotification';
import { useAuth } from './contexts/AuthContext';
import useMediaQuery from './hooks/useMediaQuery';
import DesktopShell from './components/layout/DesktopShell';
import featureFlags from './config/featureFlags';

// Lazy load all page components for code splitting
const Homepage = lazy(() => import("./components/pages/Homepage"));
const RadarFeed = lazy(() => import("./components/pages/RadarFeed"));
const ProfilePage = lazy(() => import("./components/pages/ProfilePage"));
const ProfileEditPage = lazy(() => import("./components/pages/ProfileEditPage.jsx"));
const PublicProfilePage = lazy(() => import("./components/pages/PublicProfilePage"));
const VenuePublicPage = lazy(() => import("./components/pages/VenuePublicPage"));
const VenuePortalDashboard = lazy(() => import("./components/venue-portal/VenuePortalDashboard"));

const SafetyPage = lazy(() => import("./components/pages/SafetyPage"));
const CommunityGuidelines = lazy(() => import("./components/CommunityGuidelines"));
const SafetyGuidelines = lazy(() => import("./components/SafetyGuidelines"));
const AboutPage = lazy(() => import("./components/pages/AboutPage"));

const DiscoveryFeed = lazy(() => import("./components/pages/DiscoveryFeed"));
const TonightFeed = lazy(() => import("./components/pages/TonightFeed"));
const AuthRedirect = lazy(() => import("./components/pages/AuthRedirect"));
const AuthCallback = lazy(() => import("./components/AuthCallback"));
const DesktopHomepageReplica = lazy(() => import("./components/pages/DesktopHomepageReplica"));
const LoggedInHome = lazy(() => import("./components/pages/LoggedInHome"));
const AdminDashboard = lazy(() => import("./components/pages/AdminDashboard"));
const TermsPage = lazy(() => import("./components/TermsPage"));
const PrivacyPage = lazy(() => import("./components/PrivacyPage"));
const FAQPage = lazy(() => import("./components/pages/FAQPage"));
const InviteGate = lazy(() => import("./components/invite/InviteGate"));
const InviteCodeInput = lazy(() => import("./components/invite/InviteCodeInput"));
const FirstLoginFlow = lazy(() => import("./components/onboarding/FirstLoginFlow"));
// Temporarily disabled before event - uncomment to re-enable Google One Tap
// import { useGoogleOneTap } from './hooks/useGoogleOneTap';

const DEV = process.env.NODE_ENV === 'development';
// Note: the invite gate no longer reads a build-time INVITE_ONLY_MODE env
// flag. The gate is driven entirely by the backend (see AppContent):
// session.invite_required for signed-in users, and the public
// /api/public/invite-count flag for signed-out visitors.

// Suppress noisy browser warnings that are expected/known
if (typeof window !== 'undefined') {
  const originalError = console.error;
  console.error = (...args) => {
    const message = args[0];
    // Suppress ResizeObserver loop errors (browser quirk, not actionable)
    if (typeof message === 'string' && message.includes('ResizeObserver')) {
      return;
    }
    // Suppress OAuth PKCE errors (expected during Google sign-in callback)
    if (typeof message === 'string' && (
      message.includes('code verifier') ||
      message.includes('code_verifier') ||
      (message.includes('OAuth exchange error') && message.includes('validation_failed')) ||
      (message.includes('invalid request') && message.includes('code verifier')) ||
      (message.includes('invalid request') && message.includes('code_verifier'))
    )) {
      return;
    }
    // Suppress OAuth PKCE error objects
    if (typeof args[0] === 'object' && args[0]?.message && (
      args[0].message.includes('code verifier') ||
      args[0].message.includes('code_verifier') ||
      (args[0].code === 'validation_failed' && args[0].message.includes('invalid request'))
    )) {
      return;
    }
    // Suppress button nesting warnings (React dev warning, not critical)
    if (typeof message === 'string' && message.includes('validateDOMNesting') && message.includes('button')) {
      return;
    }
    originalError.apply(console, args);
  };
}

/** Legacy URL — same inbox as /matches; preserves ?demo=buzzing etc. */
function MatchesCompareRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/matches${search}`} replace />;
}

function PublicProfileRedirect() {
  return <Navigate to="/" replace />;
}

function UsernameSettingsRedirect() {
  return <Navigate to="/profile/edit#username" replace />;
}

function InviteRedeemRoute() {
  const { code = '' } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, loading, signInWithGoogle, refreshSession } = useAuth();
  let storedCode = '';
  try {
    storedCode = sessionStorage.getItem('stagster_pending_invite_code') || '';
  } catch (_) {}
  const inviteCode = code || storedCode;

  useEffect(() => {
    if (inviteCode) {
      try {
        sessionStorage.setItem('stagster_pending_invite_code', inviteCode.toUpperCase());
      } catch (_) {}
    }
  }, [inviteCode]);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return (
      <div className="invite-gate">
        <div className="invite-gate__blur-bg">
          <Homepage2026 />
        </div>
        <div className="invite-gate__modal-overlay">
          <div className="invite-gate__glass-card">
            <Logo size="small" className="mx-auto flex justify-center mb-6 logo-glow" />
            <h2 className="invite-code__title">Sign in to claim your invite</h2>
            <p className="invite-code__subtitle">
              Use Google sign-in first. Your invite code will be waiting here after you return.
            </p>
            <button type="button" className="invite-gate__code-submit-btn" onClick={signInWithGoogle}>
              Continue with Google
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <InviteCodeInput
      initialCode={inviteCode}
      onSuccess={async () => {
        try {
          sessionStorage.removeItem('stagster_pending_invite_code');
        } catch (_) {}
        await refreshSession?.();
        navigate('/', { replace: true });
      }}
    />
  );
}

function AppContent() {
  const { showInfo } = useToast();
  const { isAuthenticated, user, loading: authLoading, refreshSession } = useAuth();
  const { unified, refreshUnified } = useUnified();
  const [activeTab, setActiveTab] = useState('home');
  const [isSlideOutOpen, setIsSlideOutOpen] = useState(false);
  const [releaseNotes, setReleaseNotes] = useState([]);
  const [showWhatsNew, setShowWhatsNew] = useState(false);
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const lastPathnameRef = useRef(location.pathname);
  const navigate = useNavigate();
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [authPromptContext, setAuthPromptContext] = useState(null);
  const [showInviteFriends, setShowInviteFriends] = useState(false);
  const [showFirstLoginFlow, setShowFirstLoginFlow] = useState(false);
  const [unreadRadarCount, setUnreadRadarCount] = useState(0);
  const [showAskStagster, setShowAskStagster] = useState(false);
  const [showPassportModal, setShowPassportModal] = useState(false);
  const [showFastLaneModal, setShowFastLaneModal] = useState(false);
  const [fastLaneVenueId, setFastLaneVenueId] = useState(null);

  useEffect(() => {
    const handleOpen = () => setShowAskStagster(true);
    const handlePassport = () => setShowPassportModal(true);
    const handleFastLane = (event) => {
      setFastLaneVenueId(event?.detail?.venueId || null);
      setShowFastLaneModal(true);
    };
    window.addEventListener('openAskStagster', handleOpen);
    window.addEventListener('openPassportModal', handlePassport);
    window.addEventListener('openFastLaneModal', handleFastLane);
    return () => {
      window.removeEventListener('openAskStagster', handleOpen);
      window.removeEventListener('openPassportModal', handlePassport);
      window.removeEventListener('openFastLaneModal', handleFastLane);
    };
  }, []);

  // Capture ?r=CODE from URL for referral attribution (global, handles any landing page)
  useEffect(() => {
    try {
      const referralParam = searchParams.get('r');
      if (referralParam) {
        referralStorage.set(referralParam);
      }
    } catch (_) {}
  }, [searchParams]);

  // Native shell (Capacitor): OAuth handoff, deep links, push (Sprints 1–2).
  // Inert on the web — isNative() guards everything inside setupNativeApp.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.Capacitor) return undefined;
    return setupNativeApp({
      onTokenStored: async (payload, next) => {
        await refreshSession();
        navigate(next && next !== '/' ? next : '/', { replace: true });
      },
      onDeepLink: (url) => {
        navigate(`${url.pathname}${url.search}`, { replace: true });
      },
      onPushRegistered: (token) => {
        registerNativePushToken(token);
      },
    });
    // The callbacks intentionally close over the current refreshSession/navigate;
    // the shell listeners are registered once per app lifecycle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch unread radar interaction count for Feed tab badge
  useEffect(() => {
    if (!isAuthenticated || !user?.id) return;
    const token = user?.access_token;
    let cancelled = false;
    const load = async () => {
      try {
        const d = await inboxHomeAPI.getHome({ token, userId: user.id });
        if (!cancelled && d?.ok && typeof d.unread_count === 'number') {
          setUnreadRadarCount(d.unread_count);
        }
      } catch { /* noop */ }
    };
    load();
    const t = setInterval(load, 30_000);
    return () => { cancelled = true; clearInterval(t); };
  }, [isAuthenticated, user?.id, user?.access_token]);

  // Check if user needs first-login onboarding (phone + gender + age)
  const needsOnboarding =
    isAuthenticated &&
    unified?.user &&
    unified.user.onboarding_state !== 'completed' &&
    (!unified.user.phone_number || !unified.user.gender || !unified.user.age);
  
  useEffect(() => {
    if (needsOnboarding && !showFirstLoginFlow) {
      setShowFirstLoginFlow(true);
    }
  }, [needsOnboarding, showFirstLoginFlow]);

  const TAB_QUERY_ALLOWED = useMemo(
    () => new Set(['home', 'stag', 'radar', 'work', 'matches']),
    [],
  );
  /** Single source of truth for shell tab on `/` — avoids a race where URL sync set `home` before `?tab=` applied. */
  const viewTab = useMemo(() => {
    const q = searchParams.get('tab');
    if (location.pathname === '/' && q && TAB_QUERY_ALLOWED.has(q)) {
      return q;
    }
    return activeTab;
  }, [searchParams, location.pathname, activeTab, TAB_QUERY_ALLOWED]);

  useLayoutEffect(() => {
    const prev = lastPathnameRef.current;
    if (location.pathname === prev) return;
    if (location.pathname.startsWith('/u/') && prev && prev !== location.pathname) {
      try {
        sessionStorage.setItem('stagster_public_profile_back', prev);
      } catch (_) {}
    }
    lastPathnameRef.current = location.pathname;
  }, [location.pathname]);



  const isDesktopPreview = location.pathname.startsWith('/desktop-preview') || location.pathname.startsWith('/desktop');
  const isDesktopViewport = useMediaQuery('(min-width: 1024px)');
  const isDesktopShellEligible = !location.pathname.startsWith('/desktop');
  const useDesktopShell = isDesktopViewport && isDesktopShellEligible;
  const showSlideOutMenu = !isDesktopPreview;

  // Google One Tap initialization (disabled - uncomment useGoogleOneTap import above to re-enable)
  // const { initializeOneTap } = useGoogleOneTap();

  // SECURITY: Global URL monitoring to prevent token exposure
  useEffect(() => {
    const checkForTokens = () => {
      if (window.location.hash && window.location.hash.includes('access_token')) {
        console.warn('🚨 SECURITY: Token detected in URL, cleaning immediately');
        window.history.replaceState(null, '', window.location.pathname);
      }
    };

    // Check immediately
    checkForTokens();

    // Monitor URL changes
    const handleHashChange = () => {
      checkForTokens();
    };

    window.addEventListener('hashchange', handleHashChange);
    
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  // Initialize Google One Tap on public pages
  // DISABLED: Temporarily disabled before event - can re-enable by uncommenting below
  // useEffect(() => {
  //   // Skip if loading, authenticated, or on admin pages
  //   if (authLoading || isAuthenticated) return;
  //   if (location.pathname.startsWith('/admin')) return;

  //   // Wait a bit for page to load, then initialize One Tap
  //   const timer = setTimeout(() => {
  //     initializeOneTap();
  //   }, 1000); // 1 second delay to let page render

  //   return () => clearTimeout(timer);
  // }, [authLoading, isAuthenticated, location.pathname, initializeOneTap]);

  // Listen for SW update notifications and prompt user to reload
  useEffect(() => {
    const handler = (e) => {
      try {
        const registration = e.detail?.registration;
        const waiting = registration?.waiting;
        if (!waiting) return;
        showInfo('A new version is available.', {
          title: 'Update ready',
          duration: 10000,
          action: {
            label: 'Reload now',
            onClick: () => {
              try {
                localStorage.setItem('fs_show_whats_new', '1');
                waiting.postMessage({ type: 'SKIP_WAITING' });
              } catch (_) {}
              setTimeout(() => window.location.reload(), 250);
            }
          }
        });
      } catch (_) {}
    };
    window.addEventListener('sw-update-available', handler);
    return () => window.removeEventListener('sw-update-available', handler);
  }, [showInfo]);

  const helloWorldApi = async () => {
    try {
      // In development, validate local API connectivity via proxy
      if (DEV) {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 2000);
        try {
          const response = await fetch(`/api/`, { signal: controller.signal });
          clearTimeout(timeout);
          if (response.ok) {
            const data = await response.json().catch(() => ({}));
            console.log(data.message || 'API OK');
          }
        } finally {
          clearTimeout(timeout);
        }
      }
    } catch (e) {
      // Silently handle backend connectivity checks (504/timeout/connection refused/network error)
      // Backend may be starting up or temporarily unavailable; no need to log
    }
  };

  useEffect(() => {
    helloWorldApi();
  }, []);

  // Prefetch release notes JSON
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/release-notes.json', { cache: 'no-store' });
        if (!res.ok) return;
        const json = await res.json();
        if (!cancelled) {
          const items = Array.isArray(json?.items) ? json.items : [];
          setReleaseNotes(items);
        }
      } catch (_) {}
    })();
    return () => { cancelled = true; };
  }, []);
  // Show What's New after an update or when version changes
  useEffect(() => {
    try {
      const currentVersion = process.env.REACT_APP_APP_VERSION || 'dev';
      const storedVersion = localStorage.getItem('fs_app_version');
      const flag = localStorage.getItem('fs_show_whats_new');
      if (flag === '1') {
        setShowWhatsNew(true);
        localStorage.removeItem('fs_show_whats_new');
      } else if (storedVersion && storedVersion !== currentVersion) {
        setShowWhatsNew(true);
      }
      localStorage.setItem('fs_app_version', currentVersion);
    } catch (_) { /* no-op */ }
  }, []);

  // Sync bottom-nav highlight with pathname and ?tab= (same rules on / and deep links)
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    const allowed = ['home', 'stag', 'radar', 'work', 'matches'];

    const moreMenuPaths = new Set([
      '/faq',
      '/safety',
      '/about',
      '/terms',
      '/privacy',
      '/community-guidelines',
      '/safety-guidelines',
    ]);

    const pathToTab = {
      '/work': 'work',
      '/stag-venues': 'stag',
      '/radar': 'radar',
      '/matches': 'matches',
      '/profile': 'home',
      '/profile/edit': 'home',
    };

    const tabForPath = pathToTab[location.pathname];
    if (tabForPath) {
      setActiveTab(tabForPath);
      setIsSlideOutOpen(false);
      return;
    }

    if (moreMenuPaths.has(location.pathname)) {
      setActiveTab('matches');
      setIsSlideOutOpen(false);
      return;
    }

    if (location.pathname === '/') {
      if (tabFromUrl && allowed.includes(tabFromUrl)) {
        setActiveTab(tabFromUrl);
      } else {
        setActiveTab('home');
      }
      setIsSlideOutOpen(false);
      return;
    }

    if (tabFromUrl && allowed.includes(tabFromUrl)) {
      setActiveTab(tabFromUrl);
      setIsSlideOutOpen(false);
    }
  }, [searchParams, location.pathname]);

  const triggerAuthPrompt = useCallback((context) => {
    if (isAuthenticated) return false;
    setAuthPromptContext(context);
    setShowAuthPrompt(true);
    return true;
  }, [isAuthenticated]);

  useEffect(() => {
    const handleExternalAuthPrompt = (event) => {
      const context = event?.detail?.context || 'explore';
      triggerAuthPrompt(context);
    };
    window.addEventListener('openAuthPrompt', handleExternalAuthPrompt);
    return () => window.removeEventListener('openAuthPrompt', handleExternalAuthPrompt);
  }, [triggerAuthPrompt]);

  useEffect(() => {
    const handleOpenMoreMenu = () => {
      setIsSlideOutOpen(true);
    };
    window.addEventListener('openMoreMenu', handleOpenMoreMenu);
    return () => window.removeEventListener('openMoreMenu', handleOpenMoreMenu);
  }, []);

  useEffect(() => {
    const handleOpenInviteFriends = () => {
      setIsSlideOutOpen(false);
      setShowInviteFriends(true);
    };
    window.addEventListener('openInviteFriends', handleOpenInviteFriends);
    return () => window.removeEventListener('openInviteFriends', handleOpenInviteFriends);
  }, []);

  const switchToTab = useCallback(
    (tabId) => {
      if (tabId === 'matches') {
        setIsSlideOutOpen(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        navigate(`/?tab=matches`);
        return;
      }
      setIsSlideOutOpen(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      navigate(`/?tab=${tabId}`);
    },
    [navigate],
  );

  const TabNavigation = () => {
    const tabs = [
      { id: 'home', label: 'Home', icon: Home },
      { id: 'stag', label: 'Bars', icon: Beer, requiresAuth: true },
      { id: 'radar', label: 'Radar', icon: Radar, requiresAuth: true },
      { id: 'work', label: 'Work', icon: Briefcase, requiresAuth: true },
      { id: 'matches', label: 'Feed', icon: MessageCircle, badgeCount: unreadRadarCount }
    ];

    const navActiveId = isSlideOutOpen ? 'matches' : viewTab;
    const activeIndex = tabs.findIndex((tab) => tab.id === navActiveId);
    const navActiveIndex = activeIndex >= 0 ? activeIndex : 0;
    const navStyles = { '--tab-count': String(tabs.length) };

    return (
      <nav
        className="fridaystag-bottom-nav"
        aria-label="Primary"
        data-active={navActiveId}
        data-active-index={navActiveIndex}
        style={navStyles}
      >
        <div className="fridaystag-nav-rail">
          {tabs.map((tab) => {
            const IconComponent = tab.icon;
            const isActive = viewTab === tab.id;
            const badgeValue = typeof tab.badgeCount === 'number' && tab.badgeCount > 0
              ? (tab.badgeCount > 9 ? '9+' : String(tab.badgeCount))
              : null;
            const isLocked = !isAuthenticated && tab.requiresAuth;

            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => switchToTab(tab.id)}
                className={`fridaystag-nav-item ${isActive ? 'is-active' : ''} ${isLocked ? 'is-locked' : ''}`}
                data-tab={tab.id}
                aria-label={tab.label}
                aria-current={isActive ? 'page' : undefined}
              >
                <span className={`fridaystag-nav-icon-shell${badgeValue ? ' has-badge' : ''}${isLocked ? ' has-lock' : ''}`}>
                  <IconComponent
                    size={22}
                    strokeWidth={1.9}
                    className="fridaystag-nav-icon"
                  />
                  {badgeValue && (
                    <span className={`fridaystag-nav-badge ${tab.badgeTone ? `fridaystag-nav-badge--${tab.badgeTone}` : ''}`}>
                      {badgeValue}
                    </span>
                  )}
                  {isLocked && (
                    <span className="fridaystag-nav-lock">
                      <Lock size={10} strokeWidth={2.5} />
                    </span>
                  )}
                </span>
                <span className="fridaystag-nav-label">{tab.label}</span>
              </button>
            );
          })}
        </div>
      </nav>
    );
  };

  const MobileShell = ({ children, variant = 'default', includeNav = true, includeFooter = true }) => {
    if (variant === 'friday') {
      return (
        <div className="fridaystag-container">
          <div className="fridaystag-mobile-shell fridaystag-mobile-shell--aligned">
            <div className="fridaystag-mobile-shell__body pb-16">
              {children}
              {includeFooter && <SharedFooter compact className="fridaystag-shared-footer" />}
            </div>
            {includeNav && <TabNavigation />}
          </div>
        </div>
      );
    }

    return (
      <div className="fridaystag-container">
        <div className="fridaystag-mobile-column mx-auto w-full max-w-[440px] box-border px-3 pb-16 pt-2">
          {children}
          {includeFooter && <SharedFooter compact className="fridaystag-shared-footer" />}
        </div>
        {includeNav && <TabNavigation />}
      </div>
    );
  };

  const renderShell = (
    content,
    {
      mobileVariant = 'default',
      includeNav = true,
      includeFooter = false,
      desktopMainClassName = '',
      /** When false, wide viewports use DesktopShell (full-width main). Default: same 440px mobile column as tabs. */
      forceMobileOnDesktop = true,
    } = {}
  ) => {
    if (useDesktopShell && !forceMobileOnDesktop) {
      return (
        <DesktopShell mainClassName={desktopMainClassName}>
          {content}
        </DesktopShell>
      );
    }
    return (
      <MobileShell variant={mobileVariant} includeNav={includeNav} includeFooter={includeFooter}>
        {content}
      </MobileShell>
    );
  };

  const renderActiveTab = () => {
    switch (viewTab) {
      case 'home':
        return isAuthenticated
          ? <LoggedInHome onSwitchTab={switchToTab} />
          : <Homepage />;
      case 'stag':
        return <DiscoveryFeed mode="stag" />;
      case 'radar':
        return <RadarFeed />;
      case 'work':
        return <DiscoveryFeed mode="work" />;
      case 'matches':
        return <TonightFeed />;
      default:
        return isAuthenticated
          ? <LoggedInHome onSwitchTab={switchToTab} />
          : <Homepage />;
    }
  };

  const RequireAuth = ({ children, context = 'explore' }) => {
    const navigate = useNavigate();

    useEffect(() => {
      if (!authLoading && !isAuthenticated) {
        // Trigger auth prompt and redirect to home
        triggerAuthPrompt(context);
        navigate('/', { replace: true });
      }
      // RequireAuth is defined inside AppContent, so it remounts on every
      // render and the outer-scope values are intentionally omitted from deps.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [context, navigate]);

    if (authLoading) {
      return (
        <div className="fridaystag-container">
          <div className="pb-20">
            <LoadingSpinner />
          </div>
        </div>
      );
    }

    if (!isAuthenticated) {
      return (
        <div className="fridaystag-container">
          <div className="pb-20">
            <LoadingSpinner />
          </div>
        </div>
      );
    }

    return children;
  };

  // Invite gate, session- and backend-driven (no build-time env flag):
  // signed-in users are gated by the server-computed invite_required /
  // invite_status on their session; signed-out visitors consult the public
  // /api/public/invite-count flag and default to gated (fail closed) until
  // the backend says otherwise.
  const [publicInviteOnly, setPublicInviteOnly] = useState(() => {
    try {
      const raw = localStorage.getItem("stagster_invite_only_flag");
      if (raw === "true") return true;
      if (raw === "false") return false;
    } catch (_) {}
    return null; // unknown: fail closed
  });

  useEffect(() => {
    let cancelled = false;
    fetch(`${getAPIRoot()}/public/invite-count`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((body) => {
        if (cancelled || !body || typeof body.invite_only !== "boolean") return;
        setPublicInviteOnly(body.invite_only);
        try {
          if (body.invite_only) {
            localStorage.setItem("stagster_invite_only_flag", "true");
          } else {
            localStorage.removeItem("stagster_invite_only_flag");
          }
        } catch (_) {}
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const inviteBypassPath =
    location.pathname.startsWith('/invite') ||
    location.pathname.startsWith('/auth') ||
    location.pathname.startsWith('/auth-callback') ||
    location.pathname.startsWith('/admin') ||
    location.pathname.startsWith('/venue-portal') ||
    ['/terms', '/privacy', '/about', '/faq', '/safety', '/community-guidelines', '/safety-guidelines'].includes(location.pathname);

  const role = user?.role || 'user';

  const privileged = role === 'admin' || role === 'founder' || role === 'moderator';
  // Fail closed: a session counts as accepted only when the server says so
  // via both invite_status and its computed invite_required flag.
  const isInviteAccepted =
    user?.invite_status === 'accepted' && user?.invite_required !== true;
  const inviteRequired =
    !inviteBypassPath &&
    !privileged &&
    (isAuthenticated ? !isInviteAccepted : publicInviteOnly !== false);

  if (inviteRequired) {
    let pendingCode = '';
    try {
      pendingCode = sessionStorage.getItem('stagster_pending_invite_code') || '';
    } catch (_) {}

    return (
      <ErrorBoundary>
        <div className="App">
          <Suspense fallback={<LoadingSpinner />}>
            {authLoading ? (
              <LoadingSpinner />
            ) : isAuthenticated ? (
              <InviteCodeInput
                initialCode={pendingCode}
                onSuccess={async () => {
                  try {
                    sessionStorage.removeItem('stagster_pending_invite_code');
                  } catch (_) {}
                  await refreshSession?.();
                  navigate('/', { replace: true });
                }}
              />
            ) : (
              <InviteGate />
            )}
          </Suspense>
        </div>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <div className="App">
          <Suspense fallback={<LoadingSpinner />}>
            <Routes>
            <Route path="/" element={renderShell(renderActiveTab())} />
            <Route path="/desktop" element={<DesktopHomepageReplica />} />
            <Route path="/auth" element={<AuthRedirect />} />
            <Route path="/safety" element={renderShell(<SafetyPage />, { includeFooter: true })} />
            <Route path="/community-guidelines" element={renderShell(<CommunityGuidelines />, { includeFooter: true })} />
            <Route path="/safety-guidelines" element={renderShell(<SafetyGuidelines />, { includeFooter: true })} />
            <Route path="/about" element={renderShell(<AboutPage />, { includeFooter: true })} />
            <Route path="/work" element={renderShell(<DiscoveryFeed mode="work" />)} />
            <Route path="/faq" element={renderShell(<FAQPage />, { includeFooter: true })} />
            <Route path="/terms" element={renderShell(<TermsPage />, { includeFooter: true })} />
            <Route path="/privacy" element={renderShell(<PrivacyPage />, { includeFooter: true })} />
            <Route path="/invite/:code" element={<InviteRedeemRoute />} />
            <Route path="/invite" element={<InviteRedeemRoute />} />

            <Route path="/auth-callback" element={<AuthCallback />} />
            <Route path="/stag-venues" element={renderShell(<DiscoveryFeed mode="stag" />)} />
            <Route path="/discover" element={renderShell(<DiscoveryFeed mode="stag" />)} />
            <Route path="/radar" element={renderShell(<RadarFeed />)} />
            <Route
              path="/profile"
              element={(
                <RequireAuth context="explore">
                  {renderShell(<ProfilePage />)}
                </RequireAuth>
              )}
            />
            <Route
              path="/profile/edit"
              element={(
                <RequireAuth context="explore">
                  {renderShell(<ProfileEditPage />)}
                </RequireAuth>
              )}
            />
            <Route
              path="/u/:username"
              element={
                featureFlags.public_profile_u
                  ? renderShell(<PublicProfilePage />, { includeNav: false })
                  : <PublicProfileRedirect />
              }
            />
            <Route path="/venue/:venueId" element={<VenuePublicPage />} />
            <Route
              path="/settings/username"
              element={
                <RequireAuth context="profile">
                  {renderShell(<UsernameSettingsRedirect />)}
                </RequireAuth>
              }
            />
            <Route path="/matches" element={renderShell(<TonightFeed />)} />
            <Route path="/matches/compare" element={<MatchesCompareRedirect />} />
            <Route path="/admin" element={<AdminDashboard />} />
            {/* Venue portal: staff sign in with the Google account ops linked
                to their venue (users.venuePortalActive + users.venueId). The
                401 handlers inside the dashboard bounce back to the login. */}
            <Route path="/venue-portal" element={<VenuePortalLogin />} />
            <Route path="/venue-portal/login" element={<VenuePortalLogin />} />
            <Route
              path="/venue-portal/dashboard"
              element={
                <Suspense fallback={<LoadingSpinner />}>
                  <VenuePortalDashboard />
                </Suspense>
              }
            />
            <Route path="*" element={renderShell(renderActiveTab())} />
            </Routes>
          </Suspense>
          
          {showSlideOutMenu && (
            <>
              <SlideOutMenu
                isOpen={isSlideOutOpen}
                onClose={() => setIsSlideOutOpen(false)}
              />
            </>
          )}
          
          {/* SOS moved to slide-out menu */}

          {!isDesktopPreview && (
            <>
              {/* What's New modal */}
              <WhatsNewModal open={showWhatsNew} onClose={() => setShowWhatsNew(false)} items={releaseNotes} />
            </>
          )}

          {/* Auth Prompt for Guest Navigation - Respectful Conversion Pattern */}
          {!isDesktopPreview && (
            <AuthPrompt
              open={showAuthPrompt}
              onOpenChange={setShowAuthPrompt}
              origin={authPromptContext}
              blur={true}
              allowClose={true}
            />
          )}

          {!isDesktopPreview && isAuthenticated ? <CheckInFollowupInbox /> : null}

          {/* Announcement banner — active, audience-matched announcements from the backend */}
          {!isDesktopPreview && isAuthenticated ? <AnnouncementPrompt /> : null}

          {/* Invite Friends Modal */}
          <InviteFriendsModal open={showInviteFriends} onClose={() => setShowInviteFriends(false)} />

          {/* Ask STAGSTER search bot overlay */}
          <AskStagsterSheet isOpen={showAskStagster} onClose={() => setShowAskStagster(false)} />

          {/* STAGSTER Nightlife Passport & Memory Vault Overlay */}
          <StagsterPassportModal isOpen={showPassportModal} onClose={() => setShowPassportModal(false)} />

          {/* FastLane & Venue Check-In Modal for Staff */}
          <FastLaneCheckinModal isOpen={showFastLaneModal} onClose={() => setShowFastLaneModal(false)} venueId={fastLaneVenueId} />

          {/* First Login Flow (gender + age) */}
          <Suspense fallback={null}>
            <FirstLoginFlow
              open={showFirstLoginFlow}
              user={user}
              onComplete={async () => {
                setShowFirstLoginFlow(false);
                await refreshUnified?.(true);
              }}
            />
          </Suspense>
      </div>
    </ErrorBoundary>
  );
};

const App = () => {
  React.useEffect(() => {
    try {
      window.sessionStorage.removeItem('fs_chunk_reload_once');
    } catch (_) {}
  }, []);

  return (
    <BrowserRouter>
      <HelmetProvider>
        <ThemeProvider>
          <UnifiedProvider>
            <ToastProvider>
              <CityProvider>
                <AppContent />
              </CityProvider>
            </ToastProvider>
          </UnifiedProvider>
        </ThemeProvider>
      </HelmetProvider>
    </BrowserRouter>
  );
};

export default App;
